<script lang="ts">
import type { TIconValue } from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

/**
 * A generic button.
 */
export default defineComponent({
  inheritAttrs: false,
  props: {
    /**
     * When true will switch out the button content for a spinner icon.
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * If set, the button will become an anchor tag, otherwise a button.
     *
     */
    route: {
      type: [String, Object] as PropType<RouteLocationRaw>,
    },
    /**
     * If set, the button will become an anchor tag, otherwise a button.
     */
    href: {
      type: String,
    },
    /**
     * On hover text.
     */
    title: {
      type: String,
      required: false,
    },
    /**
     * An icon to display next to the button content.
     */
    icon: {
      type: String as PropType<TIconValue | null>,
      default: null,
    },
    /**
     * An icon to display before the button content.
     */
    prefixIcon: {
      type: String as PropType<TIconValue | null>,
      default: null,
    },
    /**
     * Sets the size of the button icon.
     */
    iconSize: {
      type: String,
      default: 'sm',
    },
    tooltip: {
      type: Object,
      required: false,
    },
    flipIconHorizontally: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    disabled(): boolean {
      return this.$attrs.disabled === true || this.$attrs.disabled === 'true'
    },
  },
  methods: {
    disabledNoFollow(event: MouseEvent) {
      if (this.disabled) {
        event.preventDefault()
      }
    },
  },
})
</script>

<template>
  <a v-if="href" v-tooltip="tooltip" :href v-bind="$attrs" class="button">
    <div v-if="isLoading" class="spinner" />

    <template v-if="!isLoading">
      <!-- @slot Content of the button. -->
      <slot>
        <!-- @slot Content to display if a user is logged in. -->
        <slot v-if="$userStore.isLoggedIn" name="loggedIn" />

        <!-- @slot Content to display if a user is logged out. -->
        <slot v-if="$userStore.isLoggedOut" name="loggedOut" />
      </slot>
    </template>

    <template v-if="!isLoading && icon">
      <AppIcon
        :class="flipIconHorizontally ? 'rotate-180' : ''"
        class="hidden transition-all duration-200 ease-in-out sm:flex"
        :icon
        :size="iconSize"
      />
    </template>
  </a>

  <!-- @vue-skip  -->
  <RouterLink
    v-if="route"
    v-tooltip="tooltip"
    :to="route"
    :title
    v-bind="$attrs"
    class="button"
    role="button"
    tabindex="0"
    @click.capture="disabledNoFollow"
  >
    <div v-if="isLoading" class="spinner" />

    <template v-if="!isLoading && prefixIcon">
      <AppIcon
        :class="flipIconHorizontally ? 'rotate-180' : ''"
        class="hidden transition-all duration-200 ease-in-out sm:flex"
        :icon="prefixIcon"
        :size="iconSize"
      />
    </template>

    <template v-if="!isLoading">
      <!-- @slot Content of the button. -->
      <slot>
        <!-- @slot Content to display if a user is logged in. -->
        <slot v-if="$userStore.isLoggedIn" name="loggedIn" />

        <!-- @slot Content to display if a user is logged out. -->
        <slot v-if="$userStore.isLoggedOut" name="loggedOut" />
      </slot>
    </template>

    <template v-if="!isLoading && icon">
      <AppIcon
        :class="flipIconHorizontally ? 'rotate-180' : ''"
        class="hidden transition-all duration-200 ease-in-out sm:flex"
        :icon
        :size="iconSize"
      />
    </template>
  </RouterLink>

  <button
    v-if="!route && !href"
    v-tooltip="tooltip"
    :title
    :type="($attrs.type as any) ?? 'button'"
    class="button"
    v-bind="$attrs"
    tabindex="0"
  >
    <div v-if="isLoading" class="spinner" />

    <template v-if="!isLoading && prefixIcon">
      <AppIcon
        :class="flipIconHorizontally ? 'rotate-180' : ''"
        class="hidden transition-all duration-200 ease-in-out sm:flex"
        :icon="prefixIcon"
        :size="iconSize"
      />
    </template>

    <template v-if="!isLoading">
      <!-- @slot Content of the button. -->
      <slot>
        <!-- @slot Text to display if a user is logged in. -->
        <slot v-if="$userStore.isLoggedIn" name="loggedIn" />

        <!-- @slot Text to display if a user is logged out. -->
        <slot v-if="$userStore.isLoggedOut" name="loggedOut" />
      </slot>
    </template>

    <template v-if="!isLoading && icon">
      <AppIcon
        :class="flipIconHorizontally ? 'rotate-180' : ''"
        class="hidden transition-all duration-200 ease-in-out sm:flex"
        :icon
        :size="iconSize"
      />
    </template>
  </button>
</template>
